import { useState } from "react";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import api from "/imports/api/client";

const CLOSED = { open: false, target: null, preview: null, loading: false, submitting: false };

/**
 * Taking a club-placed booking off a pack purchase cancels it. Left on the
 * calendar unpaid, it kept the trainer's time (or the class seat) blocked for
 * a session the client had just said they don't want.
 *
 * The cancel is the dashboard's own: same preview, same endpoint, so the
 * club-only switch and the late-cancel window apply here exactly as they do
 * there. When the club's rules refuse it, the client may still leave it out of
 * the pack, and it stays booked and unpaid as before.
 *
 * `target`: { bookishId, label, kind: "lesson" | "class", outsidePack } plus
 * whatever the caller needs back in `onCancelled` / `onLeftOut`. `outsidePack`
 * marks a club booking this pack isn't paying for — there is nothing to leave
 * out, so a refusal has no fallback.
 */
export default function useClubBookingCancel({ onCancelled, onLeftOut }) {
  const { slug } = useWidget();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState(CLOSED);

  const openFor = async (target) => {
    setState({ ...CLOSED, open: true, target, loading: true });
    try {
      const preview = await api.getBookingRefundPreview(slug, target.bookishId);
      setState((s) => (s.target === target ? { ...s, preview, loading: false } : s));
    } catch (err) {
      setState((s) => (s.target === target ? CLOSED : s));
      enqueueSnackbar(err.message || "Couldn't check this booking — please try again", { variant: "error" });
    }
  };

  const close = () => {
    if (state.submitting) return;
    setState(CLOSED);
  };

  const confirmCancel = async () => {
    const { target } = state;
    if (!target) return;
    setState((s) => ({ ...s, submitting: true }));
    try {
      await api.cancelBooking(slug, target.bookishId);
      setState(CLOSED);
      onCancelled(target);
      enqueueSnackbar(
        target.kind === "class" ? "Class cancelled — your seat is back on the timetable" : "Lesson cancelled — that time is back on the schedule",
        { variant: "success" }
      );
    } catch (err) {
      setState((s) => ({ ...s, submitting: false }));
      enqueueSnackbar(err.message || "Failed to cancel booking", { variant: "error" });
    }
  };

  const leaveOut = () => {
    const { target } = state;
    setState(CLOSED);
    if (target) onLeftOut(target);
  };

  return { state, openFor, close, confirmCancel, leaveOut };
}
